import { useMemo } from 'react';
import {
	FeeTierDisplayUnit,
	FeeTierTableRow,
	StakedTierDiscount,
} from 'src/components/Modals/FeeTierModal/types';
import { FeeTier } from '@drift-labs/sdk';
import { VOLUME_TIERS } from 'src/components/Modals/FeeTierModal/consts';

interface Params {
	feeTiers: FeeTier[] | undefined;
	displayUnit: FeeTierDisplayUnit;
	selectedStakedTierDiscount: StakedTierDiscount;
}

export default function useFeeTierModalTable({
	feeTiers,
	displayUnit,
	selectedStakedTierDiscount,
}: Params) {
	const rows = useMemo(() => {
		const tableRows: FeeTierTableRow[] = [];

		if (!feeTiers) {
			return tableRows;
		}

		VOLUME_TIERS.forEach(({ title, display }, index) => {
			const feeTier = feeTiers[index];

			if (!feeTier || !feeTier.feeNumerator) {
				return null;
			}

			const takerFee = feeTier.feeNumerator / feeTier.feeDenominator;
			const makerFee = -(
				feeTier.makerRebateNumerator / feeTier.makerRebateDenominator
			);

			const takerFeeWithDiscount = takerFee * (1 - selectedStakedTierDiscount);
			const makerFeeWithDiscount = makerFee * (1 + selectedStakedTierDiscount);

			tableRows.push({
				tier: title,
				volume: `${!index ? '<' : '≥'} ${display}`,
				takerFee: getFeeString(takerFeeWithDiscount, displayUnit, false),
				makerFee: getFeeString(makerFeeWithDiscount, displayUnit, true),
			});
		});

		// The last row we add is for HLM fees, which is the first tier * 2 for taker.
		const firstTier = feeTiers[0];
		const hlmTakerFee = (firstTier.feeNumerator / firstTier.feeDenominator) * 2;
		const hlmMakerFee = -(
			firstTier.makerRebateNumerator / firstTier.makerRebateDenominator
		);
		tableRows.push({
			tier: '--',
			volume: '',
			takerFee: getFeeString(hlmTakerFee, displayUnit, false),
			makerFee: getFeeString(hlmMakerFee, displayUnit, true),
		});

		return tableRows;
	}, [feeTiers, displayUnit, selectedStakedTierDiscount]);

	return { rows };
}

function getFeeString(
	fee: number,
	displayUnit: FeeTierDisplayUnit,
	isMaker: boolean
) {
	return displayUnit === 'percentage'
		? `${(fee * 100).toFixed(isMaker ? 5 : 4)}%`
		: `${(fee * 10000).toFixed(isMaker ? 3 : 2)} bps`;
}
